import { configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { combineReducers } from '@reduxjs/toolkit';
import authReducer from './slices/authSlice';
import recipeReducer from './slices/recipeSlice';
import creatorReducer from './slices/creatorSlice';
import equipmentReducer from './slices/equipmentSlice';
import uiReducer from './slices/uiSlice';
import searchReducer from './slices/searchSlice';
import intelligentSearchReducer from './slices/intelligentSearchSlice';
import journeyReducer from './slices/journeySlice';
import aiProfileReducer from './slices/aiProfileSlice';
import smartDashboardReducer from './slices/smartDashboardSlice';
import recommendationEngineReducer from './slices/recommendationEngineSlice';
import notificationIntelligenceReducer from './slices/notificationIntelligenceSlice';
import dashboardPersonalizationReducer from './slices/dashboardPersonalizationSlice';
import adaptiveFilteringReducer from './slices/adaptiveFilteringSlice';
import userReducer from './slices/userSlice';
import notificationReducer from './slices/notificationSlice';
import contextualTriggersReducer from './slices/contextualTriggersSlice';
import notificationAnalyticsReducer from './slices/notificationAnalyticsSlice';
import adaptiveProgressReducer from './slices/adaptiveProgressSlice';
import progressInsightsReducer from './slices/progressInsightsSlice';
import peerMatchingReducer from './slices/peerMatchingSlice';
import studyGroupReducer from './slices/studyGroupSlice';
import socialLearningAnalyticsReducer from './slices/socialLearningAnalyticsSlice';
import premiumAnalyticsReducer from './slices/premiumAnalyticsSlice';
import aiTutoringReducer from './slices/aiTutoringSlice';

// Persist configuration
// Only persist user preferences and auth state for offline access
// Theme preference will be available immediately on app restart
const persistConfig = {
  key: 'root',
  storage: AsyncStorage,
  whitelist: ['user', 'auth'], // Only persist user preferences and auth state
  version: 1,
};

const rootReducer = combineReducers({
  auth: authReducer,
  recipes: recipeReducer,
  creators: creatorReducer,
  equipment: equipmentReducer,
  ui: uiReducer,
  search: searchReducer,
  intelligentSearch: intelligentSearchReducer,
  journey: journeyReducer,
  aiProfile: aiProfileReducer,
  smartDashboard: smartDashboardReducer,
  recommendationEngine: recommendationEngineReducer,
  notificationIntelligence: notificationIntelligenceReducer,
  notificationAnalytics: notificationAnalyticsReducer,
  dashboardPersonalization: dashboardPersonalizationReducer,
  adaptiveFiltering: adaptiveFilteringReducer,
  user: userReducer,
  notifications: notificationReducer,
  contextualTriggers: contextualTriggersReducer,
  adaptiveProgress: adaptiveProgressReducer,
  progressInsights: progressInsightsReducer,
  peerMatching: peerMatchingReducer,
  studyGroup: studyGroupReducer,
  socialLearningAnalytics: socialLearningAnalyticsReducer,
  premiumAnalytics: premiumAnalyticsReducer,
  aiTutoring: aiTutoringReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  devTools:
    typeof __DEV__ !== 'undefined'
      ? __DEV__
      : process.env.NODE_ENV !== 'production',
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
